/**
 * Starting kits — docs/05 §4 (the Ash and Codex tracks), docs/14 §8.2.
 *
 * A kit is the loadout a run *begins* with, and it is the only thing that
 * changes what you are holding on frame one. Eight unlocks have granted
 * `{ kind: 'kit' }` since the progression table was written and no kit table
 * existed anywhere in `src/`, so all eight bought nothing — the exact failure
 * class docs/13 §3 catalogues.
 *
 * Two rules shape the table:
 *
 *   1. **A kit grants exactly what its unlock text promises, and nothing it
 *      cannot deliver.** The Breaker's Kit says "hammer and scattergun", so it
 *      hands over `hammer` and `scattergun` weapons even though Salvage
 *      normally gates both — the kit *is* the grant, bought on a different
 *      track. Where the authored text promised a system that does not exist
 *      (flasks, reagent selection, a free dash charge) the promise was rewritten
 *      in `progression.ts` rather than shipped unimplemented.
 *
 *   2. **Decoupled by design**, matching its neighbours: weapons and modules are
 *      plain string ids and this file imports nothing from combat/. `tests/ids`
 *      is what makes that safe — it resolves every id here against the real
 *      catalogues, including whether a kit's modules physically fit its main
 *      hand's sockets.
 *
 * The altar still runs for a kitted run. Its role changes from "your only
 * weapon" to "an upgrade decision against something you already hold", which is
 * a better use of the 30-second reroll clock than a first-pick lottery.
 */

export interface KitDef {
  id: string;
  name: string;
  desc: string;
  /** Main-hand weapon def id. Null means the altar is still your first pick. */
  main: string | null;
  /** Off-hand weapon def id. Never null — nobody starts defenceless. */
  off: string;
  /** Module def ids, pre-installed into the main hand where sockets allow. */
  modules: string[];
}

/**
 * The kit every profile owns. `ash.kit.wanderer` is `defaultUnlocked`, costs 0,
 * and is the floor `unlockedKitIds` force-includes — a save that lost it (content
 * drift, a hand-edited profile) must still be able to start a run.
 */
export const DEFAULT_KIT = 'kit.wanderer';

/**
 * Authored order, and therefore listing order on the run-setup screen: the four
 * Ash kits (bought) before the four Codex kits (earned by discovering matter).
 */
export const KITS: readonly KitDef[] = [
  {
    id: 'kit.wanderer',
    name: "Wanderer's Kit",
    desc: 'Sword and pistol. The kit you always have.',
    main: 'ashen_sword',
    off: 'service_pistol',
    modules: [],
  },
  {
    id: 'kit.breaker',
    name: "Breaker's Kit",
    desc: 'Hammer and scattergun, wound tight around an impact core. Start loud.',
    main: 'shatter_hammer',
    off: 'breach_scattergun',
    modules: ['kin_core_impact'],
  },
  {
    id: 'kit.duelist',
    name: "Duelist's Kit",
    desc: 'Dagger and pistol, with a recoil module that turns every shot into distance.',
    main: 'flense_dagger',
    off: 'service_pistol',
    modules: ['kin_kinetic_recoil'],
  },
  {
    id: 'kit.alchemist',
    name: "Alchemist's Kit",
    desc: 'An alchemical sprayer and an etching core. Everything you meet is a solvent problem.',
    main: 'alchem_sprayer',
    off: 'service_pistol',
    modules: ['corr_core_etch'],
  },
  {
    id: 'kit.reagent',
    name: 'Reagent Kit',
    desc: 'Sword and pistol, tagged for reagent work: an etching core and a seeding payload.',
    main: 'ashen_sword',
    off: 'service_pistol',
    modules: ['corr_core_etch', 'fungal_payload_seeding'],
  },
  {
    id: 'kit.pyre',
    name: 'Pyre-tagged Kit',
    desc: 'A PYRO-tagged loadout — ember core, backdraft payload. You bring the accelerant.',
    main: 'ashen_sword',
    off: 'service_pistol',
    modules: ['pyre_core_ember', 'pyre_payload_backdraft'],
  },
  {
    id: 'kit.cryo',
    name: 'Cryo-tagged Kit',
    desc: 'A CRYO-tagged dagger. Freeze, then shatter.',
    main: 'flense_dagger',
    off: 'service_pistol',
    modules: ['frost_core_rime', 'frost_payload_shatter'],
  },
  {
    id: 'kit.void',
    name: 'Void-tagged Kit',
    desc: 'A VOID-tagged gauntlet. The Choir will notice.',
    main: 'sunder_gauntlet',
    off: 'service_pistol',
    modules: ['void_core_phase', 'void_payload_collapse'],
  },
];

export const KIT_BY_ID: Readonly<Record<string, KitDef>> = Object.fromEntries(
  KITS.map((k) => [k.id, k]),
);

export const KIT_IDS: readonly string[] = KITS.map((k) => k.id);

/**
 * Resolve a kit id, never throwing.
 *
 * A run that cannot resolve its kit must still start with something in its
 * hands — the same argument `unlockedArchetypeIds` makes for forcing the default
 * body in. An unknown id costs the player their choice, not the run.
 */
export function getKit(id: string | null | undefined): KitDef {
  return (id && KIT_BY_ID[id]) || KIT_BY_ID[DEFAULT_KIT];
}
